import { useAgents } from '#~/agent/hooks.js';
import { locateProxy } from '#~/agent/utils.js';
import { useAnalytics } from '#~/analytics/index.js';
import { createSettingsLifecycleTracker } from '#~/analytics/domains/index.js';
import { Lang } from '#~/control/calibration/index.js';
import { Sel, STag } from '#~/stag/index.js';
import type { Size, Variant } from '@studio/design-system/button';
import { Button } from '@studio/design-system/button';
import { Busy, Icon } from '@studio/design-system/icon';
import { isCalibrated, isCalibrating, Status } from '@studio/models/control/calibration';
import { StatusPowerState } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { isNotNil } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { twMerge } from 'cn';
import { shallow } from 'zustand/shallow';
import { useStore } from '../store/hooks.js';
import { exportAdvancedSettingsToCSV } from '../utils/export-settings.js';
import { useWiring } from '../wiring.js';
import type { ActionProps } from './types.js';
import { config } from '#~/config/index.js';

/** `<Actions>` component. */
export const Actions = ({ className, ...props }: ActionProps): ReactElement => {
  const wiring = useWiring();
  const { track } = useAnalytics();
  const settingsLifecycleTracker = useMemo(() => createSettingsLifecycleTracker(track), [track]);
  const [disablePowerControl, setDisablePowerControl] = useState<boolean>(false);

  const wrapTemporarilyDisablePowerControls =
    (fn: () => void): (() => void) =>
    () => {
      setDisablePowerControl(true);

      setTimeout(() => {
        setDisablePowerControl(false);
      }, 10_000);

      fn();
    };

  const { id, agentId, agentStatus, calibration, isControllable, protocol, isOther, powerState } =
    useStore((µ) => {
      const { status } = µ.external;

      return {
        id: µ.candidate.id,
        agentId: status.device?.agentId,
        agentStatus: status.device?.agentStatus,
        calibration: status.control?.calibration,
        isControllable: isNotNil(µ.candidate.control?.protocol),
        isOther: µ.manufacturer.isOther,
        protocol: µ.candidate.control?.protocol,
        powerState: status.device?.powerState
      };
    }, shallow);

  const agent = useAgents((a) => a.find((a_) => a_.id === agentId), shallow);

  const isReady = agentStatus === 'online' && powerState === StatusPowerState.POWER_STATE_ON;

  const bRunning = isReady && isCalibrating(calibration?.status);
  const genericCalibration = calibration?.status === Status.CALIBRATION_STATE_OK_GENERIC;
  const bCalibrated = isCalibrated(calibration?.status);

  const proxy = locateProxy(id);
  const canPan =
    !!proxy?.state.status?.device[id]?.capabilities?.movement?.vectors?.pan?.absoluteMove;
  const canTilt =
    !!proxy?.state.status?.device[id]?.capabilities?.movement?.vectors?.tilt?.absoluteMove;

  const canCalibrate = canPan && canTilt && !agent?.embedded;

  const passthru = {
    size: 'lg',
    variant: 'secondary'
  } satisfies { size: Size; variant: Variant };
  /*
  const egressProps = {
    size: 'sm',
    variant: 'tertiary'
  } satisfies { size: Size; variant: Variant };
*/

  const handleExportSettings = async (): Promise<void> => {
    settingsLifecycleTracker.trackAdvancedSettingsExported({
      surface: 'video_settings_header',
      section: 'iris_connection',
      export_format: 'csv',
      device_id: id
    });

    await exportAdvancedSettingsToCSV(id);
  };

  const isDev = !config.isProduction;

  return (
    <div className={twMerge('flex gap-12', className)} {...props}>
      {isDev &&
        // @ts-ignore
        false && (
          <Button
            onClick={handleExportSettings}
            {...passthru}
            {...STag(Sel.VideoSettings, 'export-advanced-settings')}
          >
            <Icon icon='settings' />
            Export Settings
          </Button>
        )}
      {!!agent?.embedded && (
        <Button onClick={wiring.handleUnlink} {...passthru} variant='danger'>
          <Icon icon='trash' /> Unlink Device
        </Button>
      )}

      {!agent?.embedded && (
        <Button
          onClick={wiring.handleRemove}
          {...passthru}
          variant='danger'
          {...STag(Sel.VideoSettings, 'delete-source')}
        >
          <Icon icon='trash' /> Delete Source
        </Button>
      )}

      {!!isReady && canCalibrate && !isOther && (!bCalibrated || genericCalibration) && (
        <Button
          className={twMerge(
            isControllable && (!bCalibrated || genericCalibration) && 'border-warning text-warning'
          )}
          disabled={bRunning || !isControllable}
          onClick={wiring.handleCalibration}
          {...passthru}
        >
          {bRunning ? (
            <>
              <Busy />
              {Lang.calibration(calibration)}
            </>
          ) : !isControllable ? (
            <>
              <Icon icon='source' /> Calibrate
            </>
          ) : genericCalibration ? (
            <>
              <Icon icon='alert-triangle' /> Calibration Required
            </>
          ) : !bCalibrated ? (
            <>
              <Icon icon='alert-triangle' />{' '}
              {isOther ? 'Calibration Required' : 'Start Calibration'}
            </>
          ) : (
            <>
              <Icon icon='source' /> Recalibrate
            </>
          )}
        </Button>
      )}

      {protocol && (
        <Button disabled={disablePowerControl} onClick={wiring.handleReboot} {...passthru}>
          <Icon icon='reboot' /> Reboot
        </Button>
      )}

      {[
        StatusPowerState.POWER_STATE_UNKNOWN,
        StatusPowerState.POWER_STATE_POWERING_OFF,
        StatusPowerState.POWER_STATE_STANDBY
      ].includes(powerState ?? StatusPowerState.POWER_STATE_UNKNOWN) && protocol ? (
        <Button
          disabled={
            disablePowerControl ||
            (powerState !== StatusPowerState.POWER_STATE_STANDBY &&
              powerState !== StatusPowerState.POWER_STATE_UNKNOWN)
          }
          onClick={wrapTemporarilyDisablePowerControls(wiring.handleWake)}
          {...passthru}
        >
          <Icon icon='power' /> Wake Up
        </Button>
      ) : (
        <Button
          disabled={disablePowerControl || powerState !== StatusPowerState.POWER_STATE_ON}
          onClick={wrapTemporarilyDisablePowerControls(wiring.handleSleep)}
          {...passthru}
        >
          <Icon icon='sleep' /> Sleep
        </Button>
      )}
      {/*
      <div>
        <Button onClick={wiring.handleStartEgress} {...egressProps}>
          Start Ndi Egress
        </Button>
        <Button onClick={wiring.handleStopEgress} {...egressProps}>
          Stop Ndi Egress
        </Button>
      </div>
      */}
    </div>
  );
};
